import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2,
  CheckCircle,
  Calendar,
  MapPin,
  Sparkles,
  Star,
  Edit,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyEvent = {
  title: '',
  date: '',
  location: '',
  description: '',
  badge: '',
  link: '/courses',
  active: true,
  order: 0
};

const EventsManager = ({ user }) => {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({ ...emptyEvent });
  const [isNew, setIsNew] = useState(false);
  const [newType, setNewType] = useState('upcoming');

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/events`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFeatured(data.featured || []);
        setUpcoming(data.upcoming || []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const startEdit = (event) => {
    setEditingEvent(event.id);
    setEditForm({ ...event });
    setIsNew(false);
  };

  const startNew = (type) => {
    setNewType(type);
    setEditForm({ ...emptyEvent, type, order: type === 'featured' ? featured.length + 1 : upcoming.length + 1 });
    setEditingEvent('new');
    setIsNew(true);
  };

  const cancelEdit = () => {
    setEditingEvent(null);
    setEditForm({ ...emptyEvent });
    setIsNew(false);
  };

  const saveEvent = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await fetch(`${BACKEND_URL}/api/admin/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ...editForm, type: newType })
        });
        if (res.ok) {
          await fetchEvents();
          cancelEdit();
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        }
      } else {
        const res = await fetch(`${BACKEND_URL}/api/admin/events/${editingEvent}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(editForm)
        });
        if (res.ok) {
          await fetchEvents();
          cancelEdit();
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        }
      }
    } catch (err) {
      console.error('Error saving event:', err);
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/events/${eventId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) await fetchEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const toggleActive = async (event) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/events/${event.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ active: !event.active })
      });
      await fetchEvents();
    } catch (err) {
      console.error('Error toggling event:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const renderEditForm = () => (
    <Card className="bg-gray-900/80 border-amber-500/30 mb-6">
      <CardContent className="p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white">
          {isNew ? `New ${newType === 'featured' ? 'Featured' : 'Upcoming'} Event` : 'Edit Event'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-300">Title</Label>
            <Input
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Event title"
              data-testid="event-title-input"
            />
          </div>
          <div>
            <Label className="text-gray-300">Date</Label>
            <Input
              value={editForm.date}
              onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="e.g. 24 - 25 Jan 2026"
              data-testid="event-date-input"
            />
          </div>
          <div>
            <Label className="text-gray-300">Location</Label>
            <Input
              value={editForm.location}
              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="e.g. Online, Hybrid, Live Event"
              data-testid="event-location-input"
            />
          </div>
          <div>
            <Label className="text-gray-300">Link</Label>
            <Input
              value={editForm.link}
              onChange={(e) => setEditForm({ ...editForm, link: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="/courses or external URL"
              data-testid="event-link-input"
            />
          </div>
          <div>
            <Label className="text-gray-300">Badge (optional)</Label>
            <Input
              value={editForm.badge}
              onChange={(e) => setEditForm({ ...editForm, badge: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="e.g. Limited seats, New"
              data-testid="event-badge-input"
            />
          </div>
          <div>
            <Label className="text-gray-300">Order</Label>
            <Input
              type="number"
              value={editForm.order}
              onChange={(e) => setEditForm({ ...editForm, order: parseInt(e.target.value) || 0 })}
              className="bg-gray-800 border-gray-700 text-white"
              data-testid="event-order-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-gray-300">Description (for featured events)</Label>
          <textarea
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-md p-3 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-amber-500"
            placeholder="Brief description of the event..."
            data-testid="event-description-input"
          />
        </div>
        <div className="flex gap-3">
          <Button onClick={saveEvent} disabled={saving || !editForm.title} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-event-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="ghost" onClick={cancelEdit} className="text-gray-400 hover:text-white">
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderEventCard = (event) => (
    <div key={event.id} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg group" data-testid={`event-${event.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className={`text-white font-medium truncate ${!event.active ? 'opacity-50' : ''}`}>{event.title}</p>
          {event.badge && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">{event.badge}</Badge>}
          {!event.active && <Badge className="bg-gray-700 text-gray-400 text-xs">Hidden</Badge>}
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{event.date}</span>
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <Switch checked={event.active} onCheckedChange={() => toggleActive(event)} data-testid={`toggle-${event.id}`} />
        <Button variant="ghost" size="sm" onClick={() => startEdit(event)} className="text-gray-400 hover:text-white" data-testid={`edit-${event.id}`}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => deleteEvent(event.id)} className="text-gray-400 hover:text-red-400" data-testid={`delete-${event.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 pt-20" data-testid="events-manager">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Events Manager</h1>
              <p className="text-gray-400 text-sm">Manage "Coming Up Next" and "All Upcoming Events" on the homepage</p>
            </div>
          </div>
          {saved && (
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle className="w-5 h-5" />
              <span>Saved!</span>
            </div>
          )}
        </div>

        {/* Edit Form */}
        {editingEvent && renderEditForm()}

        {/* Featured Events */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-white">Coming Up Next (Featured)</h2>
            </div>
            <Button size="sm" onClick={() => startNew('featured')} className="bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20" data-testid="add-featured-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Featured
            </Button>
          </div>
          <div className="space-y-3">
            {featured.length > 0 ? featured.map(renderEventCard) : (
              <p className="text-gray-500 text-center py-6">No featured events. Add one to highlight on the homepage.</p>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-white">All Upcoming Events</h2>
            </div>
            <Button size="sm" onClick={() => startNew('upcoming')} className="bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20" data-testid="add-upcoming-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Upcoming
            </Button>
          </div>
          <div className="space-y-3">
            {upcoming.length > 0 ? upcoming.map(renderEventCard) : (
              <p className="text-gray-500 text-center py-6">No upcoming events. Add events to show on the homepage.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsManager;
